"use client";

import { type Dispatch, type SetStateAction, createContext, useContext } from "react";
import type { ApiData, CabinetSchemaType } from "../components/renderer/data";
import type {
	CompartmentType,
	HorizontalSide,
	Side,
	VerticalSide,
} from "../components/renderer/data/Compartment";
import type { DoorSide } from "../components/renderer/data/Door";

export type CabinetState = CabinetSchemaType;

export type OuterConstraintsType = {
	width: { min: number; max: number };
	height: { min: number; max: number };
	depth: { min: number; max: number };
};

interface CabinetStoreInterface {
	api?: ApiData;
	setApi: Dispatch<SetStateAction<ApiData | undefined>>;

	state: CabinetState;
	setStore: Dispatch<SetStateAction<CabinetState>>;

	undo: () => void;
	redo: () => void;
	undoDisabled: boolean;
	redoDisabled: boolean;

	getOuterConstraints: (material: string, thickness: number) => OuterConstraintsType;
	setThickness: (state: CabinetState, newThickness: number, newMaterial?: string) => void;
	setMaterial: (value: string) => void;

	splitVertically: (compartment: CompartmentType) => void;
	splitVerticallyRecursivelyOnResize: (
		compartments: Map<string, CompartmentType>,
		side: VerticalSide,
		state: CabinetState,
	) => Map<string, CompartmentType>;
	splitHorizontally: (compartment: CompartmentType) => void;
	splitHorizontallyRecursivelyOnResize: (
		compartments: Map<string, CompartmentType>,
		state: CabinetState,
	) => Map<string, CompartmentType>;

	addShelf: (compartment: CompartmentType) => void;
	removeShelf: (compartment: CompartmentType) => void;

	addDoor: (compartment: CompartmentType, side: DoorSide) => void;
	removeDoor: (compartment: CompartmentType) => void;
	addDoubleDoor: (compartment: CompartmentType) => void;

	mergeCompartment: (compartment: CompartmentType, side: Side) => void;
	mergeCompartmentsOnResize: (
		compartments: Map<string, CompartmentType>,
		compartment: CompartmentType,
		side: Side,
		state: CabinetState,
	) => Map<string, CompartmentType>;
	updateCompartmentWidth: (compartment: CompartmentType, side: VerticalSide, diff: number) => void;
	updateCompartmentHeight: (
		compartment: CompartmentType,
		side: HorizontalSide,
		diff: number,
	) => void;
}

export const CabinetStoreContext = createContext<CabinetStoreInterface>(
	{} as CabinetStoreInterface,
);

export function useCabinetStore() {
	const context = useContext(CabinetStoreContext);

	if (context === null) throw new Error("CabinetStore context is not available");

	return context;
}
